import { ArrowRight, Check, Clock, Info, X } from "lucide-react";
import { BTN_PRIMARY, SECTION_LABEL } from "@/lib/site";
import { track } from "@/lib/track";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const BEFORE = [
    "Enquiry lands in a form, an inbox and WhatsApp — whoever spots it first owns it.",
    "Hours pass before anyone replies, and the warmest leads go cold.",
    "Dates, budget and group size get chased over a dozen back-and-forth messages.",
    "Every quote is rewritten from scratch in a document or a chat thread.",
    "Follow-ups live in someone's memory, so the quiet enquiries are simply forgotten.",
];

const AFTER = [
    "Every enquiry is captured in one place with the details you actually need.",
    "An instant acknowledgement goes out while the enquiry is still fresh.",
    "Each lead is qualified and routed by service, budget and timeline.",
    "A ready draft reply or quote is waiting for your approval, not your typing.",
    "Follow-ups fire on schedule until the lead replies or the thread is closed.",
];

const Column = ({ tone, title, caption, items, icon: Icon, testId }) => {
    const dark = tone === "before";
    return (
        <div
            data-testid={testId}
            className={`relative h-full overflow-hidden rounded-[1.5rem] border p-8 sm:p-10 ${
                dark
                    ? "border-[#E7D6C8] bg-white/60"
                    : "border-[#CF4242]/25 bg-white shadow-[0_28px_60px_-28px_rgb(207,66,66,0.45)] ring-1 ring-[#CF4242]/10"
            }`}
        >
            <div className="flex items-center gap-3">
                <span
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                        dark ? "bg-[#F0E4D7] text-[#8A807A]" : "bg-[#CF4242] text-white"
                    }`}
                >
                    <Icon size={18} aria-hidden="true" />
                </span>
                <div>
                    <p
                        className={`font-heading text-lg font-semibold ${
                            dark ? "text-[#8A807A]" : "text-[#1C1917]"
                        }`}
                    >
                        {title}
                    </p>
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#A89E95]">
                        {caption}
                    </p>
                </div>
            </div>

            <ol className="relative mt-8 space-y-6">
                <span
                    className={`absolute left-[11px] top-2 h-[calc(100%-1rem)] w-px ${
                        dark ? "bg-[#E7D6C8]" : "bg-gradient-to-b from-[#CF4242] to-[#E8764A]"
                    }`}
                    aria-hidden="true"
                />
                {items.map((item) => (
                    <li key={item} className="relative flex gap-4 pl-0">
                        <span
                            className={`relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ring-4 ring-[#FDF7F2] ${
                                dark ? "bg-[#F0E4D7] text-[#A89E95]" : "bg-[#CF4242] text-white"
                            }`}
                        >
                            {dark ? (
                                <X size={12} aria-hidden="true" />
                            ) : (
                                <Check size={12} aria-hidden="true" />
                            )}
                        </span>
                        <p
                            className={`text-sm leading-relaxed ${
                                dark ? "text-[#8A807A]" : "text-[#1C1917]"
                            }`}
                        >
                            {item}
                        </p>
                    </li>
                ))}
            </ol>
        </div>
    );
};

export default function BeforeAfter() {
    return (
        <section
            id="automation"
            className="scroll-mt-20 bg-[#FDF7F2]"
            data-testid="before-after-section"
        >
            <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
                <Reveal>
                    <p className={SECTION_LABEL}>Automation, In Practice</p>
                    <h2 className="mt-4 max-w-3xl font-heading text-3xl font-medium tracking-tight text-[#1C1917] md:text-4xl lg:text-[3.25rem] lg:leading-[1.1]">
                        One enquiry.{" "}
                        <span className="font-serif italic text-[#CF4242]">Two very different weeks.</span>
                    </h2>
                    <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#57504A] md:text-lg">
                        Enquiry handling and follow-ups are where most teams quietly lose their
                        week. Here is the same enquiry, handled manually and handled with an
                        automated flow.
                    </p>
                </Reveal>

                <Stagger className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
                    <StaggerItem className="h-full">
                        <Column
                            tone="before"
                            icon={X}
                            title="Before — handled by hand"
                            caption="Manual"
                            items={BEFORE}
                            testId="before-column"
                        />
                    </StaggerItem>
                    <StaggerItem className="h-full">
                        <Column
                            tone="after"
                            icon={Check}
                            title="After — handled by the flow"
                            caption="Automated"
                            items={AFTER}
                            testId="after-column"
                        />
                    </StaggerItem>
                </Stagger>

                <Reveal delay={0.1}>
                    <div
                        className="mt-10 flex flex-col gap-5 rounded-[1.5rem] border border-[#EDE3DA] bg-white p-7 sm:flex-row sm:items-center sm:justify-between sm:p-8"
                        data-testid="before-after-estimate"
                    >
                        <div className="flex items-start gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#FBECE9]">
                                <Clock size={20} className="text-[#CF4242]" aria-hidden="true" />
                            </span>
                            <div>
                                <p className="font-heading text-lg font-semibold text-[#1C1917]">
                                    Indicative saving: 4–8 hours a week
                                </p>
                                <p className="mt-1 flex items-start gap-1.5 text-xs leading-relaxed text-[#8A807A]">
                                    <Info size={13} className="mt-0.5 shrink-0" aria-hidden="true" />
                                    Indicative range only, not a guarantee. Your actual saving is
                                    estimated together during the Discover stage, using your real
                                    enquiry volume.
                                </p>
                            </div>
                        </div>
                        <a
                            href="#contact"
                            data-testid="before-after-cta-btn"
                            onClick={() => track("cta_click", { cta: "before_after_estimate" })}
                            className={`${BTN_PRIMARY} shrink-0`}
                        >
                            Estimate Mine <ArrowRight size={16} />
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
